import React, { useState, useEffect } from "react";
import {
    makeStyles,
    useTheme,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Chip,
    Typography,
    Button,
    Box
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import {
    Edit as EditIcon,
    Add as AddIcon,
    LiveHelp,
    OndemandVideo
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";
import HelpModal from "./HelpModal";

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: theme.spacing(1, 0),
    },
    topBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
    },
    topBarLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    topBarTitle: {
        fontSize: "0.95rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        color: theme.palette.primary.main,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    topBarIcon: {
        color: theme.palette.primary.main,
        fontSize: "1.3rem",
    },
    countChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 600,
        borderRadius: 13,
        height: 26,
        border: `1px solid ${theme.palette.primary.main}4D`,
        fontSize: "0.75rem",
        padding: "0 8px",
    },
    newButton: {
        borderRadius: 8,
        fontWeight: 700,
        fontSize: "0.88rem",
        textTransform: "none",
        padding: "8px 24px",
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
        boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
        transition: "all 0.25s ease",
        whiteSpace: "nowrap",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}CC !important`,
            boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
        },
    },
    tableContainer: {
        width: '100%',
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
        ...theme.scrollbarStyles,
    },
    table: {
        width: '100%',
    },
    tableHeader: {
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        backgroundColor: theme.palette.type === "dark" ? "#1b1e24 !important" : "#F1F5F9 !important",
        color: `${theme.palette.primary.main} !important`,
        borderBottom: `2px solid ${theme.palette.primary.main}4D`,
        padding: "12px 14px",
    },
    tableRow: {
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(0, 229, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
        },
    },
    tableCell: {
        borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
        padding: "12px 14px",
        color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    },
    editButton: {
        color: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}14`,
        border: `1px solid ${theme.palette.primary.main}4D`,
        borderRadius: 8,
        padding: 6,
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}26`,
            boxShadow: `0 0 10px ${theme.palette.primary.main}66`,
        },
    },
    videoChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}4D`,
        fontWeight: 600,
        borderRadius: 6,
        fontSize: "0.8rem",
        fontFamily: "monospace",
    },
    descriptionText: {
        color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.8)" : "#334155",
        fontSize: "0.88rem",
        lineHeight: 1.4,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    emptyNotice: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.5)" : "#64748B",
        fontSize: "0.95rem",
    }
}));

export function HelpsManagerGrid({ records, onSelect }) {
    const classes = useStyles();
    const theme = useTheme();
    const isLight = theme.palette.type === "light";

    return (
        <Paper className={classes.tableContainer} elevation={0}>
            <Table className={classes.table} size="small" aria-label="helps table">
                <TableHead>
                    <TableRow>
                        <TableCell align="center" className={classes.tableHeader} style={{ width: '60px' }}>
                            {i18n.t("helps.grid.id", "#")}
                        </TableCell>
                        <TableCell align="left" className={classes.tableHeader} style={{ minWidth: 200 }}>
                            {i18n.t("helps.grid.title", "Título")}
                        </TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 160 }}>
                            {i18n.t("helps.grid.video", "Código de Video")}
                        </TableCell>
                        <TableCell align="left" className={classes.tableHeader} style={{ minWidth: 280 }}>
                            {i18n.t("helps.grid.description", "Descripción")}
                        </TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ width: '80px' }}>
                            {i18n.t("helps.grid.actions", "Acciones")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} className={classes.emptyNotice}>
                                {i18n.t("helps.grid.empty", "No hay ayudas ni tutoriales registrados.")}
                            </TableCell>
                        </TableRow>
                    ) : (
                        records.map((row) => (
                            <TableRow key={row.id} className={classes.tableRow}>
                                <TableCell align="center" className={classes.tableCell} style={{ fontWeight: 600, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
                                    {row.id}
                                </TableCell>
                                <TableCell align="left" className={classes.tableCell}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: isLight ? '#0F172A' : '#ffffff', fontSize: '0.92rem' }}>
                                        {row.title || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <Chip
                                        size="small"
                                        icon={<OndemandVideo style={{ color: theme.palette.primary.main, fontSize: 16 }} />}
                                        label={row.video || "-"}
                                        className={classes.videoChip}
                                    />
                                </TableCell>
                                <TableCell align="left" className={classes.tableCell}>
                                    <Typography className={classes.descriptionText}>
                                        {row.description || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <IconButton
                                        className={classes.editButton}
                                        onClick={() => onSelect(row)}
                                        aria-label="edit"
                                        size="small"
                                    >
                                        <EditIcon style={{ fontSize: "1.1rem" }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
}

export default function HelpsManager() {
    const classes = useStyles();
    const { list, save, update, remove } = useHelps();

    const [modalOpen, setModalOpen] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [record, setRecord] = useState({
        title: '',
        description: '',
        video: ''
    });

    useEffect(() => {
        async function fetchData() {
            await loadHelps();
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadHelps = async () => {
        setLoading(true);
        try {
            const helpList = await list();
            setRecords(helpList || []);
        } catch (e) {
            toast.error(i18n.t("helps.toasts.loadError", "Error al cargar los tutoriales de ayuda"));
        }
        setLoading(false);
    };

    const handleOpenNew = () => {
        setRecord({
            title: '',
            description: '',
            video: ''
        });
        setModalOpen(true);
    };

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            title: data.title || '',
            description: data.description || '',
            video: data.video || ''
        });
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            if (data.id !== undefined) {
                await update(data);
            } else {
                await save(data);
            }
            await loadHelps();
            setModalOpen(false);
            toast.success(i18n.t("helps.toasts.saveSuccess", "¡Tutorial guardado correctamente!"));
        } catch (e) {
            toast.error(i18n.t("helps.toasts.saveError", "Ocurrió un error al guardar el tutorial"));
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await remove(record.id);
            await loadHelps();
            setShowConfirmDialog(false);
            setModalOpen(false);
            toast.success(i18n.t("helps.toasts.deleteSuccess", "¡Tutorial eliminado correctamente!"));
        } catch (e) {
            toast.error(i18n.t("helps.toasts.deleteError", "Ocurrió un error al eliminar el tutorial"));
        }
        setLoading(false);
    };

    const handleOpenDeleteDialog = (itemToDelete) => {
        if (itemToDelete) {
            setRecord(itemToDelete);
        }
        setShowConfirmDialog(true);
    };

    return (
        <div className={classes.root}>
            {/* TOP BAR CON TÍTULO, CONTADOR Y BOTÓN + NUEVO */}
            <Paper className={classes.topBar} elevation={0}>
                <div className={classes.topBarLeft}>
                    <Typography className={classes.topBarTitle}>
                        <LiveHelp className={classes.topBarIcon} />
                        {i18n.t("helps.systemHelps", "Ayudas y Tutoriales del Sistema")}
                    </Typography>
                    <Chip
                        className={classes.countChip}
                        label={`${i18n.t("helps.registeredHelps", { count: records.length })}`}
                        size="small"
                    />
                </div>
                <Button
                    className={classes.newButton}
                    variant="contained"
                    startIcon={<AddIcon style={{ fontSize: "1.2rem", fontWeight: "bold" }} />}
                    onClick={handleOpenNew}
                >
                    {i18n.t("helps.newHelp", "Nuevo Video Tutorial")}
                </Button>
            </Paper>

            {/* TABLA GLASSMORFICA DE TUTORIALES */}
            <HelpsManagerGrid
                records={records}
                onSelect={handleSelect}
            />

            {/* MODAL FLOTANTE DE CREACIÓN / EDICIÓN */}
            <HelpModal
                open={modalOpen}
                onClose={handleCloseModal}
                initialValue={record}
                onSubmit={handleSubmit}
                onDelete={handleOpenDeleteDialog}
                loading={loading}
            />

            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
            <ConfirmationModal
                title={i18n.t("helps.deleteConfirm.title", "Eliminar Tutorial")}
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleDelete}
            >
                {i18n.t("helps.deleteConfirm.message", "¿Desea realmente eliminar este tutorial de ayuda?")}
            </ConfirmationModal>
        </div>
    );
}
